/* ==========================================================================
   Terlato Kitchen — "Find Their Gift" · console behaviour
   sections/gf-finder-console.liquid

   Three jobs, none of them matching:

   1. Publish the answers. Every radio change is written into window.GFFinder,
      which is what gf-finder-result listens to. This file never decides which
      product wins and never reads the metaobject.
   2. Keep the stepper honest — a step reads active once it is answered, and the
      first unanswered step is the one the visitor is on.
   3. Run the mobile disclosure. Below 750px the comp shows one 44px field per
      question carrying the chosen answer, not five options each; above it every
      option is on screen and there is nothing to disclose.

   Progressive enhancement: with this file absent the console is still a working
   form of real radios. Every option list stays open (the collapse is applied
   here, not in the markup), and the submit button falls through to the form's
   own action.
   ========================================================================== */

(function (window, document) {
  'use strict';

  var MOBILE_QUERY = '(max-width: 749px)';

  var SEL = {
    root: '[data-gf-finder-console]',
    form: '[data-gf-finder-form]',
    step: '[data-gf-step]',
    stepBtn: '[data-gf-step-btn]',
    question: '[data-gf-question]',
    toggle: '[data-gf-question-toggle]',
    panel: '[data-gf-question-panel]',
    value: '[data-gf-question-value]',
    option: '[data-gf-option]',
    error: '[data-gf-finder-error]'
  };

  function each(list, fn) {
    for (var i = 0; i < list.length; i++) fn(list[i], i);
  }

  function Console(root) {
    this.root = root;
    this.form = root.querySelector(SEL.form);
    this.questions = [].slice.call(root.querySelectorAll(SEL.question));
    this.steps = [].slice.call(root.querySelectorAll(SEL.step));
    this.error = root.querySelector(SEL.error);
    this.placeholder = root.getAttribute('data-gf-placeholder') || 'Choose…';
    this.anchor = root.getAttribute('data-gf-result-anchor') || '';
    this.incomplete = root.getAttribute('data-gf-incomplete-message') || '';
    this.isMobile = false;

    this.store = window.GFFinder || null;
    if (!this.store || !this.questions.length) return;

    this.register();
    this.restoreFromUrl();
    this.bind();
    this.watchViewport();
    this.sync();
  }

  /* --------------------------------------------------------------------
     Wiring
     -------------------------------------------------------------------- */

  Console.prototype.keyOf = function (question) {
    return question.getAttribute('data-gf-question-key') || '';
  };

  Console.prototype.register = function () {
    var self = this;
    each(this.questions, function (question) {
      var prompt = question.querySelector('.gf-finder-q__prompt');
      self.store.registerQuestion({
        key: self.keyOf(question),
        label: question.getAttribute('data-gf-question-label') || '',
        prompt: prompt ? prompt.textContent.trim() : ''
      });
    });
  };

  /* A shared result link carries the answers as query params. Tick the matching
     radios rather than writing straight into the store, so the DOM and the
     store cannot disagree and a value that no longer exists (an option a
     merchant deleted) simply finds no radio and is dropped. */
  Console.prototype.restoreFromUrl = function () {
    var params = this.store.answersFromUrl();
    var self = this;
    var restored = false;

    each(this.questions, function (question) {
      var key = self.keyOf(question);
      if (!params[key]) return;
      var inputs = question.querySelectorAll(SEL.option);
      each(inputs, function (input) {
        if (input.value !== params[key]) return;
        input.checked = true;
        self.publish(input);
        restored = true;
      });
    });

    /* Only a link that answered everything asks for a result. A half-filled URL
       pre-ticks what it can and waits for the visitor. */
    if (restored && this.store.isComplete()) this.store.submit();
  };

  Console.prototype.publish = function (input) {
    this.store.setAnswer(input.name, input.value, input.getAttribute('data-gf-option-label') || input.value);
  };

  Console.prototype.bind = function () {
    var self = this;

    this.root.addEventListener('change', function (event) {
      var input = event.target;
      if (!input || !input.matches || !input.matches(SEL.option)) return;

      self.publish(input);
      self.sync();
      self.clearError();

      /* On mobile the field the visitor just answered collapses back to its
         value and the next unanswered one opens — the comp's flow, and it keeps
         the card short. On desktop nothing moves; the answer is already on
         screen. */
      if (self.isMobile) {
        var question = input.closest(SEL.question);
        self.close(question);
        var next = self.firstUnanswered();
        if (next && next !== question) self.open(next);
      }
    });

    this.root.addEventListener('click', function (event) {
      var toggle = event.target.closest ? event.target.closest(SEL.toggle) : null;
      if (toggle) {
        var question = toggle.closest(SEL.question);
        if (self.isOpen(question)) {
          self.close(question);
        } else {
          self.openOnly(question);
        }
        return;
      }

      var stepBtn = event.target.closest ? event.target.closest(SEL.stepBtn) : null;
      if (stepBtn) self.goToStep(stepBtn.closest(SEL.step));
    });

    if (this.form) {
      this.form.addEventListener('submit', function (event) {
        /* The form's own action is the JavaScript-off fallback. Once we are here
           the matcher can run, so the navigation is always cancelled — even when
           the answers are incomplete, where leaving the page would lose them. */
        event.preventDefault();

        if (self.store.submit()) {
          self.clearError();
          self.jumpToResult();
        } else {
          self.showError();
        }
      });
    }
  };

  /* --------------------------------------------------------------------
     State → DOM
     -------------------------------------------------------------------- */

  Console.prototype.answeredLabel = function (question) {
    var answer = this.store.getAnswer(this.keyOf(question));
    return answer ? answer.label : null;
  };

  Console.prototype.firstUnanswered = function () {
    for (var i = 0; i < this.questions.length; i++) {
      if (!this.answeredLabel(this.questions[i])) return this.questions[i];
    }
    return null;
  };

  Console.prototype.sync = function () {
    var self = this;
    var current = this.firstUnanswered();

    each(this.questions, function (question) {
      var label = self.answeredLabel(question);
      var value = question.querySelector(SEL.value);
      if (value) value.textContent = label || self.placeholder;
      question.classList.toggle('gf-finder-q--answered', !!label);
    });

    /* A step is lit once it is answered, and the first unanswered step is lit as
       the one you are on — so the row always has exactly one leading edge. */
    each(this.steps, function (step) {
      var key = step.getAttribute('data-gf-step');
      var question = self.byKey(key);
      var on = !!(question && (self.answeredLabel(question) || question === current));

      step.classList.toggle('gf-finder-console__step--on', on);

      var chip = step.querySelector('.gf-finder-chip');
      var chipLabel = step.querySelector('.gf-finder-chip-label');
      if (chip) chip.classList.toggle('gf-finder-chip--off', !on);
      if (chipLabel) chipLabel.classList.toggle('gf-finder-chip-label--off', !on);
    });
  };

  Console.prototype.byKey = function (key) {
    for (var i = 0; i < this.questions.length; i++) {
      if (this.keyOf(this.questions[i]) === key) return this.questions[i];
    }
    return null;
  };

  /* --------------------------------------------------------------------
     Mobile disclosure

     `hidden` is only ever set while the mobile layout is active. Leaving it on
     when the viewport grows would hide every option behind a button CSS has
     already taken off the screen.
     -------------------------------------------------------------------- */

  Console.prototype.isOpen = function (question) {
    var panel = question && question.querySelector(SEL.panel);
    return !!panel && !panel.hidden;
  };

  Console.prototype.open = function (question) {
    if (!question) return;
    var panel = question.querySelector(SEL.panel);
    var toggle = question.querySelector(SEL.toggle);
    if (panel) panel.hidden = false;
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
  };

  Console.prototype.close = function (question) {
    if (!question || !this.isMobile) return;
    var panel = question.querySelector(SEL.panel);
    var toggle = question.querySelector(SEL.toggle);
    if (panel) panel.hidden = true;
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
  };

  Console.prototype.openOnly = function (question) {
    var self = this;
    each(this.questions, function (other) {
      if (other === question) self.open(other); else self.close(other);
    });
  };

  Console.prototype.setMobile = function (on) {
    if (on === this.isMobile) return;
    this.isMobile = on;

    each(this.questions, function (question) {
      var panel = question.querySelector(SEL.panel);
      var toggle = question.querySelector(SEL.toggle);
      if (!panel) return;

      if (on) {
        panel.hidden = true;
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
      } else {
        panel.hidden = false;
        /* The toggle is off-screen on desktop; an aria-expanded left behind on
           it would describe a control nobody can reach. */
        if (toggle) toggle.removeAttribute('aria-expanded');
      }
    });
  };

  Console.prototype.watchViewport = function () {
    if (!window.matchMedia) return;
    var mql = window.matchMedia(MOBILE_QUERY);
    var self = this;
    var apply = function (event) { self.setMobile(event.matches); };

    apply(mql);
    if (mql.addEventListener) {
      mql.addEventListener('change', apply);
    } else if (mql.addListener) {
      mql.addListener(apply);
    }
  };

  /* --------------------------------------------------------------------
     Submit
     -------------------------------------------------------------------- */

  Console.prototype.goToStep = function (step) {
    if (!step) return;
    var question = this.byKey(step.getAttribute('data-gf-step'));
    if (!question) return;

    if (this.isMobile) this.openOnly(question);

    var input = question.querySelector(SEL.option + ':checked') || question.querySelector(SEL.option);
    if (input) input.focus({ preventScroll: this.isMobile });
    if (!this.isMobile) window.GFFinder.scrollTo(question);
  };

  Console.prototype.showError = function () {
    if (!this.error) return;
    this.error.textContent = this.incomplete;
    this.error.hidden = false;

    var question = this.firstUnanswered();
    if (!question) return;
    if (this.isMobile) this.openOnly(question);
    var input = question.querySelector(SEL.option);
    if (input) input.focus();
  };

  Console.prototype.clearError = function () {
    if (!this.error || this.error.hidden) return;
    this.error.textContent = '';
    this.error.hidden = true;
  };

  Console.prototype.jumpToResult = function () {
    if (!this.anchor) return;
    var target = document.querySelector(this.anchor);
    /* The result section moves focus to itself once it has painted; all this
       has to do is bring it into view. If it is not on the page — a merchant
       removed it — the anchor simply finds nothing and the button is a no-op
       rather than a jump to the top. */
    if (target) window.GFFinder.scrollTo(target);
  };

  /* --------------------------------------------------------------------
     Boot
     -------------------------------------------------------------------- */

  function init(scope) {
    each((scope || document).querySelectorAll(SEL.root), function (root) {
      if (root.gfFinderConsole) return;
      root.gfFinderConsole = new Console(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  /* Theme editor: a section re-render replaces the markup, so the instance on
     the old node goes with it and the new one has to be built. */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})(window, document);
